import { useState, useEffect } from 'react';
import {
  DeepRequired,
  FieldError,
  FieldErrorsImpl,
  FieldValues,
  GlobalError,
  UseFormClearErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { Button, Form, FormAlert, FormField, FormGroup, FormLabel } from '@ax/ui';
import style from '@/modules/Auth/Login/Login.module.scss';
import { Link } from 'react-router-dom';
import { PROJECT_ENVIRONMENT } from '@ax/env';
import { SupportLink } from '@/modules/Auth/Login/SupportLink.tsx';
import UncontrolledSelectField from '@ax/ui/src/components/UncontrolledSelectField';
import { useLoginDomains } from '@/hooks/useLoginDomains';

export function UsernameDomainForm(props: {
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  message: string;
  errors: Partial<FieldErrorsImpl<DeepRequired<FieldValues>>> & {
    root?: Record<string, GlobalError> & GlobalError;
  };
  setValue: UseFormSetValue<FieldValues>;
  clearErrors: UseFormClearErrors<FieldValues>;
  renderButtonContent: JSX.Element | string;
}) {
  const { domains, addDomain, clearValue } = useLoginDomains();
  const [username, setUsername] = useState('');
  const [domain, setDomain] = useState(domains.length > 0 ? domains[0] : null);
  const [domainError, setDomainError] = useState<string>('');
  const domainRegex = /^@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  useEffect(() => {
    if (domains.length > 0 && !domain) {
      setDomain(domains[0]);
    }
  }, [domains, domain]);

  const handleDomainChange = (
    selectedOption: { value: string; label: string } | null,
    isNew: boolean,
    meta?: { action: string },
  ) => {
    console.log('Handle Domain Change', selectedOption, isNew, meta);
    if (meta?.action === 'clear-value') {
      clearValue(selectedOption?.value ?? '');
      setDomain(null);
      return;
    }

    if (selectedOption && isNew) {
      if (domainRegex.test(selectedOption.value)) {
        addDomain(selectedOption.value);
        setDomain(selectedOption.value);
        setDomainError('');
      } else {
        setDomain(null);
        setDomainError(`"${selectedOption.value}" is not a valid domain`);
      }
    } else if (selectedOption) {
      setDomain(selectedOption.value);
      setDomainError('');
    } else {
      setDomain(null);
    }
  };

  const handleSubmit = async (e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    try {
      props.clearErrors('email');
      props.setValue('email', `${username}${domain}`);
      await props.onSubmit(e);
    } catch (error) {
      console.error(error);
      // Optionally set an error state here if needed
    }
  };

  return (
    <Form
      className={style.login__form__body}
      onSubmit={handleSubmit}
    >
      <FormAlert message={props.message} />
      <div className="grid grid-cols-2 gap-2 mb-8">
        <FormGroup>
          <FormLabel
            className="input__label"
            htmlFor={'username'}
          >
            Username
          </FormLabel>
          <FormField error={props.errors.email as FieldError}>
            <input
              id={'username'}
              type="text"
              className="input"
              placeholder="Username"
              required
              onChange={(e) => setUsername(e.target.value)}
            />
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormLabel
            className="input__label"
            htmlFor={'domain'}
          >
            Domain
          </FormLabel>
          <UncontrolledSelectField
            name="domain"
            placeholder="@domain.com"
            options={domains.map((domain) => ({ value: domain, label: domain }))}
            value={(domain && { value: domain, label: domain }) || null}
            onChange={handleDomainChange}
            error={domainError}
            allowCreate
            required
          />
        </FormGroup>
        {props.errors.email && (
          <SupportLink className={style.login__form__body__username__support_link} />
        )}
      </div>

      <Button
        className="h-[40px] w-[385px]"
        variant="primary"
        block
      >
        {props.renderButtonContent}
      </Button>
      <div className={style.login__form__footer}>
        <span>Don't have an account?</span>
        <Link
          to={PROJECT_ENVIRONMENT.URL['signup-url']}
          className={style.login__form__footer__link}
          target="_blank"
        >
          Sign up
        </Link>
      </div>
    </Form>
  );
}
